import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import './SidebarTour.css';

/* ═══════════════════════════════════════════════════════════
   EVERY POSSIBLE TOUR STEP
   ─────────────────────────────────────────────────────────
   Each step carries a `visibleWhen(ctx)` predicate.
   The predicate receives the same permission / subscription
   context the real sidebar uses, so only items the user
   can actually see will appear in the tour.
═══════════════════════════════════════════════════════════ */

const ALL_TOUR_STEPS = [
   {
      target: '/clients',
      title: 'Clients',
      description:
         'Manage all your service users in one place. View profiles, care needs, key contacts and risk assessments for every client.',
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
         </svg>
      ),
      accent: '#00bfa5',
      // Sidebar: inside "Care Management" → hasPermission('Clients')
      visibleWhen: (ctx) => ctx.isCareOrAllInOne && ctx.hasPermission('Clients'),
   },
   {
      target: '/careplans',
      title: 'Care Plans',
      description:
         'Create, review and update personalised care plans. Track outcomes, set goals and ensure every client receives tailored support.',
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
         </svg>
      ),
      accent: '#10b981',
      visibleWhen: (ctx) => ctx.isCareOrAllInOne && ctx.hasPermission('Careplan'),
   },
   {
      target: '/medication',
      title: 'Medication & eMAR',
      description:
         'Electronic medication administration records. Track prescriptions, log doses, manage PRN medication and generate compliance reports.',
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6z" />
            <path d="M13.5 10.5H21A7.5 7.5 0 1 0 13.5 3v7.5z" />
         </svg>
      ),
      accent: '#8b5cf6',
      visibleWhen: (ctx) => ctx.isCareOrAllInOne && ctx.hasPermission('MedicationEmar'),
   },
   {
      target: '/incidents',
      title: 'Incidents & Alerts',
      description:
         'Record and manage incidents, safeguarding concerns and alerts. Track follow-up actions and generate reports for regulatory compliance.',
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
         </svg>
      ),
      accent: '#ef4444',
      visibleWhen: (ctx) => ctx.isCareOrAllInOne && ctx.hasPermission('IncidentAlerts'),
   },
   {
      target: '/council',
      title: 'Council',
      description:
         'Manage local authority contracts, council-funded placements and compliance documentation for commissioned care services.',
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
            <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
            <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
            <path d="M10 6h4" />
            <path d="M10 10h4" />
            <path d="M10 14h4" />
            <path d="M10 18h4" />
         </svg>
      ),
      accent: '#f59e0b',
      visibleWhen: (ctx) => ctx.isCareOrAllInOne && ctx.hasPermission('Council'),
   },
   {
      target: '/staff',
      title: 'Staff Directory',
      description:
         'View and manage your entire care team. Track qualifications, DBS checks, training records and availability all in one place.',
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
         </svg>
      ),
      accent: '#3b82f6',
      // Sidebar: employeeSubscription === 'active' || showCare || allInOne || (user && Employees)
      visibleWhen: (ctx) =>
         (ctx.employeeSubscription === 'active' || ctx.isCareOrAllInOne) && ctx.hasPermission('Employees'),
   },
   {
      target: '/documents',
      title: 'Policies & Procedures',
      description:
         "Access and manage all your organisation's policies and procedures. Keep documents up to date and ensure staff have read the latest versions.",
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
            <polyline points="13 2 13 9 20 9" />
         </svg>
      ),
      accent: '#06b6d4',
      // Sidebar: userHavelicense || (userRole === 'user' && hasPermission('Documents'))
      visibleWhen: (ctx) => ctx.userHavelicense || (ctx.userRole === 'user' && ctx.hasPermission('Documents')),
   },
   {
      target: '/myDocuments',
      title: 'My Documents',
      description:
         'Your personal document workspace. Upload, organise and manage your own files, templates and private documents securely.',
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
         </svg>
      ),
      accent: '#ec4899',
      visibleWhen: (ctx) => ctx.userHavelicense || (ctx.userRole === 'user' && ctx.hasPermission('My Documents')),
   },
   {
      target: '/users',
      title: 'System Users',
      description:
         'Manage user accounts, roles and permissions. Control who can access what across your organisation with granular permission settings.',
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="8.5" cy="7" r="4" />
            <line x1="20" y1="8" x2="20" y2="14" />
            <line x1="23" y1="11" x2="17" y2="11" />
         </svg>
      ),
      accent: '#6366f1',
      visibleWhen: (ctx) => ctx.hasPermission('Users'),
   },
   {
      target: '/settings',
      title: 'Settings',
      description:
         "Configure your organisation details, notification preferences, integrations and system-wide settings to tailor Care Daily to your needs.",
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
         </svg>
      ),
      accent: '#64748b',
      visibleWhen: (ctx) => ctx.hasPermission('Settings'),
   },
   {
      target: 'book-demo',
      title: 'Book a Demo',
      description:
         "Schedule a personalised demo with our team. We'll walk you through all features and help you get the most out of Care Daily.",
      icon: (
         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
            <path d="M6 12v5c3 3 9 3 12 0v-5" />
         </svg>
      ),
      accent: '#3b82f6',
      // Sidebar: showTrainingButton() → only within 30 days of account creation
      visibleWhen: (ctx) => ctx.showTrainingButton,
   },
];

const STORAGE_KEY = 'caredaily_tour_completed';

/* ═══════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════ */

const SidebarTour = ({ sidebarCollapsed, onExpandSidebar }) => {
   /* ── Redux selectors (same stores CareSidebar reads) ── */
   const userDetails        = useSelector((state) => state.myProfile.addViewData);
   const companyData        = useSelector((state) => state.UIReducer.userDetails);
   const active_subs        = useSelector((state) => state.userLoginData.active_subs);
   const employeeSubscription = useSelector((state) => state.userLoginData.employeeSubscription);
   const userHavelicense    = useSelector((state) => state.userLoginData.userHavelicense);

   const userRole = sessionStorage.getItem('userRole');
   const user_permission = useMemo(() => {
      try {
         return JSON.parse(sessionStorage.getItem('user_permission')) || [];
      } catch {
         return [];
      }
   }, []);

   /* ── Derive the same booleans CareSidebar uses ── */

   const showCareSection = useMemo(() => {
      if (!userDetails?._id) return false;
      const sub = userDetails?.subscription_data?.subscription_plan;
      return sub?.includes('caremanagement') || userDetails?.careManagement?.status === 1;
   }, [userDetails]);

   const showAllInOne = useMemo(() => {
      if (!userDetails?._id) return false;
      const sub = userDetails?.subscription_data?.subscription_plan;
      return sub?.includes('allinone') || userDetails?.allinOne?.status === 1;
   }, [userDetails]);

   const isCareOrAllInOne = showCareSection || showAllInOne || userRole === 'user';

   const showTrainingButton = useMemo(() => {
      if (!userDetails?.createdAt) return false;
      const cutoff = new Date(userDetails.createdAt);
      cutoff.setDate(cutoff.getDate() + 30);
      return cutoff > new Date();
   }, [userDetails]);

   /* ── NEW CUSTOMER CHECK ──
      Only show the tour for users whose account was created
      within the last 2 days (first-time / new customer login).
      Change the number below to adjust the window. */
   const isNewCustomer = useMemo(() => {
      if (!userDetails?.createdAt) return false;
      const created = new Date(userDetails.createdAt);
      const now = new Date();
      const diffDays = (now - created) / (1000 * 60 * 60 * 24);
      return diffDays <= 2;
   }, [userDetails]);

   const hasPermission = useCallback(
      (name) => {
         if (userRole === 'company') return true;
         if (userRole === 'employee' && name === 'Staff Policies') return true;
         return user_permission.some((p) => p.name === name);
      },
      [userRole, user_permission],
   );

   /* ── Build context object & filter steps ── */

   const ctx = useMemo(
      () => ({
         userRole,
         hasPermission,
         isCareOrAllInOne,
         showCareSection,
         showAllInOne,
         showTrainingButton,
         employeeSubscription,
         userHavelicense,
      }),
      [userRole, hasPermission, isCareOrAllInOne, showCareSection, showAllInOne, showTrainingButton, employeeSubscription, userHavelicense],
   );

   const visibleSteps = useMemo(
      () => ALL_TOUR_STEPS.filter((s) => s.visibleWhen(ctx)),
      [ctx],
   );

   /* ── Local state ── */
   const [isActive, setIsActive]         = useState(false);
   const [currentStep, setCurrentStep]   = useState(0);
   const [targetRect, setTargetRect]     = useState(null);
   const [isAnimating, setIsAnimating]   = useState(false);
   const [showWelcome, setShowWelcome]   = useState(false);
   const [showComplete, setShowComplete] = useState(false);
   const [tooltipPos, setTooltipPos]     = useState({ top: 0, left: 0, placement: 'right' });
   const tooltipRef = useRef(null);
   const PAD = 6;

   const hasTourSteps = visibleSteps.length > 0;

   /* ── Auto-show welcome on first visit (new customers only) ── */
   useEffect(() => {
      if (!isNewCustomer) return;
      if (!hasTourSteps) return;
      if (localStorage.getItem(STORAGE_KEY)) return;
      const t = setTimeout(() => setShowWelcome(true), 1500);
      return () => clearTimeout(t);
   }, [hasTourSteps, isNewCustomer]);

   /* ── Element finder ── */
   const findTarget = useCallback((step) => {
      if (!step) return null;
      if (step.target === 'book-demo') return document.querySelector('.care-training-btn');

      // 1. data-tour-target attribute (most reliable)
      const byAttr = document.querySelector(`[data-tour-target="${step.target}"]`);
      if (byAttr) return byAttr;

      // 2. Match by span text inside nav items
      const items = document.querySelectorAll('.care-nav-item');
      const needle = step.title.toLowerCase();
      for (const el of items) {
         const txt = el.querySelector('span')?.textContent?.trim()?.toLowerCase() || '';
         if (txt === needle) return el;
      }
      return null;
   }, []);

   /* ── Position calculator ── */
   const updateRect = useCallback(() => {
      if (!isActive || currentStep >= visibleSteps.length) return;
      const el = findTarget(visibleSteps[currentStep]);
      if (!el) { setTargetRect(null); return; }

      const r = el.getBoundingClientRect();
      setTargetRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const TW = 340, TH = 260;

      let top  = r.top + r.height / 2 - TH / 2;
      let left = r.right + 20;
      let placement = 'right';

      if (left + TW > vw) { left = r.left - TW - 20; placement = 'left'; }
      top = Math.max(16, Math.min(top, vh - TH - 16));

      setTooltipPos({ top, left, placement });
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
   }, [isActive, currentStep, visibleSteps, findTarget]);

   useEffect(() => {
      updateRect();
      window.addEventListener('resize', updateRect);
      window.addEventListener('scroll', updateRect, true);
      return () => {
         window.removeEventListener('resize', updateRect);
         window.removeEventListener('scroll', updateRect, true);
      };
   }, [updateRect]);

   /* ── Keyboard nav ── */
   useEffect(() => {
      if (!isActive) return;
      const handler = (e) => {
         if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); nextStep(); }
         else if (e.key === 'ArrowLeft')                   { e.preventDefault(); prevStep(); }
         else if (e.key === 'Escape')                      { e.preventDefault(); closeTour();  }
      };
      document.addEventListener('keydown', handler);
      return () => document.removeEventListener('keydown', handler);
   });

   /* ── Actions ── */
   const animate = (cb) => {
      setIsAnimating(true);
      setTimeout(() => { cb(); setTimeout(() => setIsAnimating(false), 400); }, 200);
   };

   const startTour = () => {
      if (!hasTourSteps) return;
      setShowWelcome(false);
      const go = () => { setCurrentStep(0); setIsActive(true); setIsAnimating(true); setTimeout(() => setIsAnimating(false), 400); };
      if (sidebarCollapsed && onExpandSidebar) { onExpandSidebar(); setTimeout(go, 350); } else { go(); }
   };

   const nextStep = () => {
      if (currentStep < visibleSteps.length - 1) animate(() => setCurrentStep((p) => p + 1));
      else finishTour(); // Last step → show completion screen
   };

   const prevStep = () => {
      if (currentStep > 0) animate(() => setCurrentStep((p) => p - 1));
   };

   const goToStep = (i) => animate(() => setCurrentStep(i));

   // Show the completion screen (user finished all steps)
   const finishTour = () => {
      setIsActive(false); setCurrentStep(0); setTargetRect(null);
      setShowComplete(true);
      localStorage.setItem(STORAGE_KEY, 'true');
   };

   // Close without completion screen (user clicked X or pressed Esc)
   const closeTour = () => {
      setIsActive(false); setCurrentStep(0); setTargetRect(null);
      localStorage.setItem(STORAGE_KEY, 'true');
   };

   const dismissComplete = () => {
      setShowComplete(false);
   };

   const dismissWelcome = () => {
      setShowWelcome(false);
      localStorage.setItem(STORAGE_KEY, 'true');
   };

   const restartTour = () => { localStorage.removeItem(STORAGE_KEY); setShowComplete(false); startTour(); };

   /* ── Render nothing when not a new customer or zero accessible steps ── */
   if (!isNewCustomer) return null;
   if (!hasTourSteps) return null;

   const step     = visibleSteps[currentStep];
   const progress = ((currentStep + 1) / visibleSteps.length) * 100;

   /* Build dynamic welcome preview from first 3 visible steps */
   const previewFeatures = visibleSteps.slice(0, 3).map((s) => ({ label: s.title, color: s.accent }));

   return (
      <>
         {/* ────────── WELCOME MODAL ────────── */}
         {showWelcome && (
            <div className="tour-welcome-overlay" onClick={dismissWelcome}>
               <div className="tour-welcome-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="tour-welcome-glow" />
                  <div className="tour-welcome-icon">
                     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                     </svg>
                  </div>
                  <h2 className="tour-welcome-title">Welcome to Care Daily</h2>
                  <p className="tour-welcome-desc">
                     Take a quick guided tour to discover the{' '}
                     <strong>{visibleSteps.length} features</strong> available to you.
                  </p>

                  <div className="tour-welcome-features">
                     {previewFeatures.map((f, i) => (
                        <div className="tour-welcome-feature" key={i}>
                           <div className="tour-welcome-feature-dot" style={{ background: f.color }} />
                           <span>{f.label}</span>
                        </div>
                     ))}
                     {visibleSteps.length > 3 && (
                        <div className="tour-welcome-feature">
                           <div className="tour-welcome-feature-dot" style={{ background: '#94a3b8' }} />
                           <span>and {visibleSteps.length - 3} more…</span>
                        </div>
                     )}
                  </div>

                  <div className="tour-welcome-actions">
                     <button className="tour-welcome-start" onClick={startTour}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                        Start Tour ({visibleSteps.length} steps)
                        <span className="tour-welcome-duration">~{Math.max(1, Math.round(visibleSteps.length * 0.2))} min</span>
                     </button>
                     <button className="tour-welcome-skip" onClick={dismissWelcome}>
                        I'll explore on my own
                     </button>
                  </div>
               </div>
            </div>
         )}

         {/* ────────── ACTIVE TOUR OVERLAY ────────── */}
         {isActive && step && (
            <div className="tour-overlay">
               {/* SVG mask spotlight */}
               <svg className="tour-overlay-svg" width="100%" height="100%">
                  <defs>
                     <mask id="tour-spotlight-mask">
                        <rect width="100%" height="100%" fill="white" />
                        {targetRect && (
                           <rect x={targetRect.left} y={targetRect.top} width={targetRect.width} height={targetRect.height} rx="10" ry="10" fill="black" />
                        )}
                     </mask>
                  </defs>
                  <rect width="100%" height="100%" fill="rgba(15,23,42,0.65)" mask="url(#tour-spotlight-mask)" />
               </svg>

               {targetRect && (
                  <div className="tour-spotlight-ring" style={{ top: targetRect.top, left: targetRect.left, width: targetRect.width, height: targetRect.height }} />
               )}

               {/* Tooltip */}
               <div ref={tooltipRef} className={`tour-tooltip ${tooltipPos.placement} ${isAnimating ? 'animating' : ''}`} style={{ top: tooltipPos.top, left: tooltipPos.left }}>
                  <button className="tour-tooltip-close" onClick={closeTour} title="End tour">
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>

                  <div className="tour-tooltip-counter">
                     <span className="tour-tooltip-step-num">{currentStep + 1}</span>
                     <span className="tour-tooltip-step-sep">/</span>
                     <span className="tour-tooltip-step-total">{visibleSteps.length}</span>
                  </div>

                  <div className="tour-tooltip-header">
                     <div className="tour-tooltip-icon" style={{ background: `${step.accent}15`, color: step.accent }}>{step.icon}</div>
                     <div>
                        <h3 className="tour-tooltip-title">{step.title}</h3>
                        <div className="tour-tooltip-accent-bar" style={{ background: step.accent }} />
                     </div>
                  </div>

                  <p className="tour-tooltip-desc">{step.description}</p>

                  <div className="tour-tooltip-progress-track">
                     <div className="tour-tooltip-progress-fill" style={{ width: `${progress}%`, background: step.accent }} />
                  </div>

                  <div className="tour-tooltip-nav">
                     <button className="tour-tooltip-btn tour-tooltip-btn-prev" onClick={prevStep} disabled={currentStep === 0}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                        Back
                     </button>

                     <div className="tour-tooltip-dots">
                        {visibleSteps.map((_, i) => (
                           <div key={i} className={`tour-tooltip-dot ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'completed' : ''}`} style={i === currentStep ? { background: step.accent } : {}} onClick={() => goToStep(i)} />
                        ))}
                     </div>

                     <button className="tour-tooltip-btn tour-tooltip-btn-next" onClick={nextStep} style={{ background: step.accent }}>
                        {currentStep === visibleSteps.length - 1 ? 'Finish' : 'Next'}
                        {currentStep < visibleSteps.length - 1
                           ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                           : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        }
                     </button>
                  </div>
               </div>

               <div className="tour-keyboard-hint">
                  Press <kbd>←</kbd> <kbd>→</kbd> to navigate · <kbd>Esc</kbd> to exit
               </div>
            </div>
         )}

         {/* ────────── COMPLETION SCREEN ────────── */}
         {showComplete && (
            <div className="tour-complete-overlay" onClick={dismissComplete}>
               <div className="tour-complete-modal" onClick={(e) => e.stopPropagation()}>

                  {/* Animated background confetti dots */}
                  <div className="tour-complete-confetti">
                     {[...Array(20)].map((_, i) => (
                        <div
                           key={i}
                           className="tour-confetti-dot"
                           style={{
                              '--x': `${Math.random() * 100}%`,
                              '--delay': `${Math.random() * 0.6}s`,
                              '--size': `${4 + Math.random() * 6}px`,
                              '--color': visibleSteps[i % visibleSteps.length]?.accent || '#00bfa5',
                              '--drift': `${-30 + Math.random() * 60}px`,
                           }}
                        />
                     ))}
                  </div>

                  {/* Success checkmark */}
                  <div className="tour-complete-check">
                     <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <circle className="tour-complete-check-circle" cx="12" cy="12" r="10" stroke="#00bfa5" strokeWidth="2" />
                        <polyline className="tour-complete-check-tick" points="7 12.5 10.5 16 17 9" stroke="#00bfa5" strokeWidth="2.5" />
                     </svg>
                  </div>

                  <h2 className="tour-complete-title">You're all set!</h2>
                  <p className="tour-complete-desc">
                     You've explored all {visibleSteps.length} features available to you.
                     You're ready to start managing your care services with Care Daily.
                  </p>

                  {/* Summary of features toured */}
                  <div className="tour-complete-summary">
                     {visibleSteps.map((s, i) => (
                        <div className="tour-complete-feature" key={i}>
                           <div className="tour-complete-feature-icon" style={{ color: s.accent, background: `${s.accent}12` }}>
                              {s.icon}
                           </div>
                           <span className="tour-complete-feature-label">{s.title}</span>
                           <svg className="tour-complete-feature-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={s.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                           </svg>
                        </div>
                     ))}
                  </div>

                  {/* Actions */}
                  <div className="tour-complete-actions">
                     <button className="tour-complete-btn-primary" onClick={dismissComplete}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                           <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                           <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                        Go to Dashboard
                     </button>
                     <button className="tour-complete-btn-secondary" onClick={restartTour}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                           <polyline points="23 4 23 10 17 10" />
                           <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                        </svg>
                        Take Tour Again
                     </button>
                  </div>
               </div>
            </div>
         )}

         {/* ────────── RESTART FAB ────────── */}
         {/* {!isActive && !showWelcome && !showComplete && (
            <button className="tour-restart-fab" onClick={restartTour} title="Restart guided tour">
               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
               <span>Tour</span>
            </button>
         )} */}
      </>
   );
};

export default SidebarTour;
export { ALL_TOUR_STEPS, STORAGE_KEY };